/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';

import GroupService from '../../../apiServices/GroupService';
import GroupMetadata from '../../../models/GroupMetadata';
import ProfileService from '../../../apiServices/ProfileService';
import { PolicyName } from '../../../models/PolicyModel';

import { selectors as policySelectors } from './policy';
import { getLocalText } from '../../../utilities/localizedUtil';

const requestStatus = {
  NONE: 'NONE',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAIL: 'FAIL',
};

export const types = {
  REQUEST: 'app/group/REQUEST',
  RECEIVE: 'app/group/RECEIVE',
  REQUEST_SEARCH: 'app/group/REQUEST_SEARCH',
  RECEIVE_SEARCH: 'app/group/RECEIVE_SEARCH',
  CLEAR_SEARCH: 'app/group/CLEAR_SEARCH',
  JOINED_SUCCESS: 'app/group/JOINED_SUCCESS',
  LEAVE_SUCCESS: 'app/group/LEAVE_SUCCESS',
  SET_ACTIVE_GROUP: 'app/group/SET_ACTIVE_GROUP',
};

const initialState = {
  isFetching: false,
  activeGroupId: -1,
  requestGroupStatus: requestStatus.NONE,
  memberships: {
    '-1': {},
  },
  lookup: {},
  searchResults: [],
  searchComplete: true,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case types.REQUEST_SEARCH:
    case types.RECEIVE_SEARCH:
    case types.CLEAR_SEARCH:
    case types.JOINED_SUCCESS:
    case types.LEAVE_SUCCESS:
    case types.SET_ACTIVE_GROUP:
    case types.REQUEST:
    case types.RECEIVE:
      return _.assign({}, state, action.payload);
    default:
      return state;
  }
}

export const selectors = {
  getAllGroups: (state) => state.groups.lookup,
  getGroupIdsForProfile: (state, profileId) => _.keys(_.get(state, `groups.memberships.${profileId}`, {}))
      .map(k => Number(k))
      .sort((id1, id2) => compareGroupsByName(state, id1, id2)),
  getActiveGroupIdsForProfile: (state, profileId) => getGroupIdsForProfileByStatus(state, profileId, true),
  getInactiveGroupIdsForProfile: (state, profileId) => getGroupIdsForProfileByStatus(state, profileId, false),
  isActiveGroup: (state, profileId, groupId) => {
    const groupsInfo = _.get(state, `groups.memberships.${profileId}`, {});
    const info = groupsInfo[groupId] || {};

    return !!info.active;
  },
  getGroup: (state, groupId) => (state.groups.lookup[groupId] || GroupMetadata.empty()),
  getGroupBySubdomain: (state, subdomain) => (_.find(state.groups.lookup, { subdomain }) || GroupMetadata.empty()),
  getSearchResults: (state) => state.groups.searchResults,
  getAllMemberships: (state) => state.groups.memberships,
  getIsSearchComplete: (state) => state.groups.searchComplete,
  isMember: (state, profileId, groupId) => _.get(state, `groups.memberships.${profileId}.${groupId}.active`, false),
  getMembership: (state, profileId, groupId) => _.get(state, `groups.memberships.${profileId}.${groupId}`, null),
  getActiveGroup: (state) => (state.groups.lookup[state.groups.activeGroupId] || GroupMetadata.empty()),
  getActiveGroupId: (state) => state.groups.activeGroupId,
  getIsRequestGroupFailed: (state) => state.groups.requestGroupStatus === requestStatus.FAIL,
};

function compareGroupsByName(state, id1, id2) {
  const name1 = selectors.getGroup(state, id1).name.default || '';
  const name2 = selectors.getGroup(state, id2).name.default || '';

  return name1.localeCompare(name2, 'en', { sensitivity: 'base', ignorePunctuation: 'true' });
}

function getGroupIdsForProfileByStatus(state, profileId, isActive) {
  const groupsInfo = _.get(state, `groups.memberships.${profileId}`, {});

  return _.keys(groupsInfo)
    .filter(k => groupsInfo[k].active === isActive)
    .map(k => Number(k))
    .sort((id1, id2) => compareGroupsByName(state, id1, id2));
}

function receiveGroups(payload) {
  return {
    type: types.RECEIVE,
    payload,
  };
}

export function receiveAndFormatGroupsForProfile(profileId, groups) {
  return (dispatch, getState) => {
    return new Promise((res) => {
      const lookup = selectors.getAllGroups(getState());
      const memberships = selectors.getAllMemberships(getState());
      const profileCollection = selectors.getGroupIdsForProfile(getState(), profileId);
      const profileMemberships = {};
      groups.forEach(g => {
        lookup[g.group.id] = _.assign({}, GroupMetadata.empty(), g.group);
        profileCollection.push(g.group.id);
        profileMemberships[g.group.id] = g;
      });
      memberships[profileId] = profileMemberships;

      dispatch(receiveGroups({
        lookup,
        memberships,
      }));

      res();
    });
  };
}

export function receiveAndFormatGroup(group) {
  return (dispatch, getState) => {
    const lookup = selectors.getAllGroups(getState());
    lookup[group.id] = _.assign({}, GroupMetadata.empty(), group);
    dispatch(receiveGroups({
      lookup,
      requestGroupStatus: requestStatus.SUCCESS,
    }));
  };
}

function requestGroup() {
  return {
    type: types.REQUEST,
    payload: {
      requestGroupStatus: requestStatus.PENDING,
    },
  };
}

function requestGroupBySubdomain(subdomain) {
  return (dispatch, getState) => {
    dispatch(requestGroup());

    GroupService.getMetadata(subdomain, (err, group) => {
      if (err) {
        dispatch(receiveGroups({
          requestGroupStatus: requestStatus.FAIL,
        }));
        return;
      }

      const lookup = selectors.getAllGroups(getState());
      lookup[group.id] = _.assign({}, GroupMetadata.empty(), group);
      dispatch(receiveGroups({
        lookup,
        requestGroupStatus: requestStatus.SUCCESS,
      }));
    });
  };
}

function requestGroupById(id) {
  return (dispatch, getState) => {
    return new Promise((res, rej) => {
      dispatch(requestGroup());

      GroupService.getMetadataById(id, (err, group) => {
        if (err) {
          dispatch(receiveGroups({
            requestGroupStatus: requestStatus.FAIL,
          }));
          rej();
          return;
        }

        const lookup = selectors.getAllGroups(getState());
        lookup[group.id] = _.assign({}, GroupMetadata.empty(), group);
        dispatch(receiveGroups({
          lookup,
          requestGroupStatus: requestStatus.SUCCESS,
        }));
        res();
      });
    });
  };
}

function requestGroups(profileId) {
  return (dispatch, getState) => {
    return new Promise((res, rej) => {
      GroupService.getGroupsForProfile(profileId, (err, groups) => {
        const lookup = selectors.getAllGroups(getState());
        const memberships = selectors.getAllMemberships(getState());
        const profileCollection = selectors.getGroupIdsForProfile(getState(), profileId);
        const profileMemberships = {};
        groups.forEach(g => {
          lookup[g.group.id] = _.assign({}, GroupMetadata.empty(), g.group);
          profileCollection.push(g.group.id);
          profileMemberships[g.group.id] = g;
        });
        memberships[profileId] = profileMemberships;

        dispatch(receiveGroups({
          lookup,
          memberships,
        }));

        if (err) {
          rej(err);
        } else {
          res();
        }
      });
    });
  };
}

function receiveSearchResults(payload) {
  return {
    type: types.RECEIVE_SEARCH,
    payload,
  };
}

function search(searchParams, languageCode) {
  return (dispatch, getState) => {
    dispatch({
      type: types.REQUEST_SEARCH,
      payload: {
        searchComplete: false,
        searchResults: [],
      },
    });
    GroupService.search(searchParams, (err, searchResults) => {
      const lookup = selectors.getAllGroups(getState());
      searchResults.forEach(s => {
        lookup[s.id] = _.assign({}, GroupMetadata.empty(), s);
      });
      const sortedResults = searchResults.sort((a, b) => {
        const aName = getLocalText(a.name, languageCode);
        const bName = getLocalText(b.name, languageCode);
        return aName.localeCompare(bName, languageCode, { sensitivity: 'case' });
      });
      dispatch(receiveSearchResults({
        query: searchParams,
        searchResults: sortedResults.map(s => s.id),
        lookup,
        searchComplete: true,
      }));
    });
  };
}

function joinGroup(profileId, groupMetadata, callback) {
  return (dispatch, getState) => {
    GroupService.joinGroup(profileId, groupMetadata, (err, newMembership) => {
      if (err) { callback(err); return; }

      const memberships = selectors.getAllMemberships(getState());
      memberships[profileId] = memberships[profileId] || {};
      memberships[profileId][newMembership.group.id] = newMembership;

      dispatch({
        type: types.JOINED_SUCCESS,
        payload: {
          memberships,
          searchResults: [],
        },
      });
      callback();
    });
  };
}

function leaveGroup(profileId, groupId, callback) {
  return (dispatch, getState) => {
    GroupService.leaveGroup(profileId, groupId, (err) => {
      if (err) { callback(err); return; }

      const memberships = selectors.getAllMemberships(getState());
      memberships[profileId] = _.omit(memberships[profileId], groupId);

      const policy = policySelectors.getPolicyByName(getState(), PolicyName.GROUP_CONSENT);
      ProfileService.withdrawGroupConsent(profileId, groupId, [policy.id]);

      dispatch({
        type: types.LEAVE_SUCCESS,
        payload: {
          memberships,
        },
      });

      callback();
    });
  };
}


function clearSearchResults() {
  return {
    type: types.CLEAR_SEARCH,
    payload: { searchResults: [] },
  };
}

function setActiveGroup(activeGroupId) {
  return {
    type: types.SET_ACTIVE_GROUP,
    payload: { activeGroupId },
  };
}


export const actions = {
  receiveGroups,
  requestGroupBySubdomain,
  requestGroupById,
  requestGroups,
  receiveSearchResults,
  search,
  clearSearchResults,
  joinGroup,
  leaveGroup,
  setActiveGroup,
};
